import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ForSaleListing } from '../types';
import { API_URL } from '../config';
import './ForSale.css';

const ForSale: React.FC = () => {
  const { user } = useAuth();
  const [listings, setListings] = useState<ForSaleListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState<ForSaleListing | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingListing, setEditingListing] = useState<ForSaleListing | null>(null);

  const fetchListings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { 
        headers: { Authorization: `Bearer ${token}` }
      };
      
      const response = await axios.get(`${API_URL}/api/forsale`, config);
      setListings(response.data.listings);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleDeleteListing = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/forsale/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchListings();
    } catch (error) {
      console.error('Failed to delete listing:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="forsale-container">
      <div className="forsale-header">
        <h1>For Sale / Lease</h1>
        <p>Browse available units in 18 GRAF ROAD</p>
        {(user?.role === 'admin' || user?.role === 'owner') && (
          <button 
            className="add-listing-btn"
            onClick={() => {
              setEditingListing(null);
              setShowModal(true);
            }}
          >
            + List Your Property
          </button>
        )}
      </div>

      <div className="listings-grid">
        {listings.length === 0 ? (
          <div className="no-listings">
            <p>No properties available at this time.</p>
          </div>
        ) : (
          listings.map((listing) => (
            <div key={listing._id} className="listing-card">
              <div className="listing-image-container">
                {listing.images.length > 0 ? (
                  <img
                    src={`${API_URL}${listing.images[0].url}`}
                    alt={`Unit ${listing.apartment}`}
                    className="listing-image"
                  />
                ) : (
                  <div className="listing-no-image">
                    <span>No Image Available</span>
                  </div>
                )}
                <span className={`listing-status ${listing.status}`}>
                  {listing.status}
                </span>
              </div>
              <div className="listing-content">
                <div className="listing-price">{formatCurrency(listing.price)}</div>
                <h3 className="listing-title">Unit {listing.apartment}</h3>
                <div className="listing-specs">
                  <span>{listing.bathrooms} bath</span>
                  <span>•</span>
                  <span>{listing.squareFeet.toLocaleString()} sqft</span>
                </div>
                <p className="listing-description">{listing.description}</p>
                {listing.features.length > 0 && (
                  <div className="listing-features">
                    {listing.features.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="feature-tag">{feature}</span>
                    ))}
                    {listing.features.length > 3 && (
                      <span className="feature-tag">+{listing.features.length - 3} more</span>
                    )}
                  </div>
                )}
                <div className="listing-actions">
                  <button 
                    className="view-details-btn"
                    onClick={() => setSelectedListing(listing)}
                  >
                    View Details
                  </button>
                  {user?.role === 'admin' && (
                    <>
                      <button 
                        className="edit-btn"
                        onClick={() => {
                          setEditingListing(listing);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteListing(listing._id)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedListing && (
        <ListingDetailsModal
          listing={selectedListing}
          onClose={() => setSelectedListing(null)}
        />
      )}

      {showModal && (
        <ListingFormModal
          listing={editingListing}
          onClose={() => {
            setShowModal(false);
            setEditingListing(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setEditingListing(null);
            fetchListings();
          }}
        />
      )}
    </div>
  );
};

// Listing Details Modal
const ListingDetailsModal: React.FC<{
  listing: ForSaleListing;
  onClose: () => void;
}> = ({ listing, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === listing.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? listing.images.length - 1 : prev - 1
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content listing-details-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        
        {listing.images.length > 0 && (
          <div className="image-gallery">
            <img
              src={`${API_URL}${listing.images[currentImageIndex].url}`}
              alt={`Unit ${listing.apartment}`}
              className="gallery-image"
            />
            {listing.images.length > 1 && (
              <>
                <button className="gallery-btn prev" onClick={prevImage}>❮</button>
                <button className="gallery-btn next" onClick={nextImage}>❯</button>
                <div className="gallery-indicators">
                  {listing.images.map((_, idx) => (
                    <span
                      key={idx}
                      className={`indicator ${idx === currentImageIndex ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(idx)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="details-content">
          <h2>Unit {listing.apartment}</h2>
          <div className="details-price">{formatCurrency(listing.price)}</div>
          
          <div className="details-specs">
            <div className="spec">
              <span className="spec-label">Bathrooms</span>
              <span className="spec-value">{listing.bathrooms}</span>
            </div>
            <div className="spec">
              <span className="spec-label">Square Feet</span>
              <span className="spec-value">{listing.squareFeet.toLocaleString()}</span>
            </div>
          </div>

          <div className="details-section">
            <h3>Description</h3>
            <p>{listing.description}</p>
          </div>

          {listing.features.length > 0 && (
            <div className="details-section">
              <h3>Features</h3>
              <ul className="features-list">
                {listing.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="details-section contact-section">
            <h3>Contact Information</h3>
            <p><strong>Name:</strong> {listing.contactName}</p>
            <p><strong>Email:</strong> <a href={`mailto:${listing.contactEmail}`}>{listing.contactEmail}</a></p>
            {listing.contactPhone && (
              <p><strong>Phone:</strong> <a href={`tel:${listing.contactPhone}`}>{listing.contactPhone}</a></p>
            )}
          </div>

          <p className="listing-date">
            Listed on {new Date(listing.listedDate).toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

// Listing Form Modal
const ListingFormModal: React.FC<{
  listing: ForSaleListing | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ listing, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    apartment: listing?.apartment || '',
    price: listing?.price?.toString() || '',
    bathrooms: listing?.bathrooms?.toString() || '',
    squareFeet: listing?.squareFeet?.toString() || '',
    description: listing?.description || '',
    features: listing?.features?.join(', ') || '',
    contactName: listing?.contactName || '',
    contactEmail: listing?.contactEmail || '',
    contactPhone: listing?.contactPhone || '',
    status: listing?.status || 'active',
  });
  const [images, setImages] = useState<File[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('apartment', formData.apartment);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('bathrooms', formData.bathrooms);
      formDataToSend.append('squareFeet', formData.squareFeet);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('features', JSON.stringify(formData.features.split(',').map(f => f.trim()).filter(f => f)));
      formDataToSend.append('contactName', formData.contactName);
      formDataToSend.append('contactEmail', formData.contactEmail);
      formDataToSend.append('contactPhone', formData.contactPhone);
      formDataToSend.append('status', formData.status);

      images.forEach((image) => {
        formDataToSend.append('images', image);
      });

      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      };

      if (listing?._id) {
        await axios.put(`${API_URL}/api/forsale/${listing._id}`, formDataToSend, config);
      } else {
        await axios.post(`${API_URL}/api/forsale`, formDataToSend, config);
      }
      onSuccess();
    } catch (error: any) {
      console.error('Failed to save listing:', error);
      const message = error.response?.data?.message || 'Failed to save listing. Please try again.';
      alert(message);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content listing-form-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{listing?._id ? 'Edit Listing' : 'Create New Listing'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Unit Number</label>
              <input
                type="text"
                value={formData.apartment}
                onChange={(e) => setFormData({ ...formData, apartment: e.target.value })}
                className={!formData.apartment.trim() ? 'empty-field' : ''}
              />
            </div>
            <div className="form-group">
              <label>Price</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className={!formData.price.trim() ? 'empty-field' : ''}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Bathrooms</label>
              <input
                type="number"
                step="0.5"
                value={formData.bathrooms}
                onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                className={!formData.bathrooms.trim() ? 'empty-field' : ''}
              />
            </div>
            <div className="form-group">
              <label>Square Feet</label>
              <input
                type="number"
                value={formData.squareFeet}
                onChange={(e) => setFormData({ ...formData, squareFeet: e.target.value })}
                className={!formData.squareFeet.trim() ? 'empty-field' : ''}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className={!formData.description.trim() ? 'empty-field' : ''}
            />
          </div>

          <div className="form-group">
            <label>Features (comma-separated)</label>
            <input
              type="text"
              value={formData.features}
              onChange={(e) => setFormData({ ...formData, features: e.target.value })}
              placeholder="e.g., Hardwood floors, Central AC, Balcony"
              className={!formData.features.trim() ? 'empty-field' : ''}
            />
          </div>

          <div className="form-group">
            <label>Images (up to 10)</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setImages(files.slice(0, 10));
              }}
            />
            <small>{images.length} image(s) selected</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact Name</label>
              <input
                type="text"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                className={!formData.contactName.trim() ? 'empty-field' : ''}
              />
            </div>
            <div className="form-group">
              <label>Contact Email</label>
              <input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                className={!formData.contactEmail.trim() ? 'empty-field' : ''}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact Phone</label>
              <input
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                className={!formData.contactPhone.trim() ? 'empty-field' : ''}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="sold">Sold</option>
              </select>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn">
              {listing?._id ? 'Update Listing' : 'Create Listing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForSale;

